import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { CalendarIcon, Plus, Users, Search, FileText, PenTool, Upload } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { DocumentRequestService } from '@/services/documentRequestService';
import { 
  CreateDocumentRequestData, 
  FamilyOption,
  COMMON_DOCUMENT_TYPES,
  CommonDocumentType,
  FormTemplate 
} from '@/types/documentRequestTypes';

interface CreateDocumentRequestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  preselectedTemplate?: FormTemplate;
}

const CreateDocumentRequestModal: React.FC<CreateDocumentRequestModalProps> = ({
  open,
  onOpenChange,
  onSuccess,
  preselectedTemplate
}) => {
  const { user } = useAuth();
  const organizationId = user?.user_metadata?.organization_id;

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [deadline, setDeadline] = useState<Date | undefined>();
  const [selectedFamilies, setSelectedFamilies] = useState<string[]>([]);
  const [selectAllFamilies, setSelectAllFamilies] = useState(false);
  const [documentType, setDocumentType] = useState<CommonDocumentType | 'custom'>('custom');
  const [signingMode, setSigningMode] = useState<'upload' | 'electronic'>('upload');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  
  // Data state
  const [families, setFamilies] = useState<FamilyOption[]>([]);
  const [classes, setClasses] = useState<{ id: string; name: string }[]>([]);
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [familySearch, setFamilySearch] = useState('');
  const [formTemplates, setFormTemplates] = useState<FormTemplate[]>([]);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [loadingFamilies, setLoadingFamilies] = useState(false);

  // Load families and classes
  useEffect(() => {
    if (!organizationId || !open) return;

    const loadData = async () => {
      setLoadingFamilies(true);
      try {
        // Load families with student details
        const { data: familiesData, error: familiesError } = await supabase
          .from('families')
          .select(`
            id,
            name,
            parents:parents(count),
            students:students(
              id,
              first_name,
              last_name,
              preferred_name
            )
          `)
          .eq('organization_id', organizationId)
          .order('name');

        if (familiesError) throw familiesError;

        const processedFamilies: FamilyOption[] = (familiesData || []).map(family => ({
          id: family.id,
          name: family.name,
          parent_count: family.parents?.length || 0,
          student_count: family.students?.length || 0,
          children: family.students?.map(student => ({
            id: student.id,
            first_name: student.first_name,
            last_name: student.last_name,
            preferred_name: student.preferred_name
          })) || []
        }));

        setFamilies(processedFamilies);

        // Load classes
        const { data: classesData, error: classesError } = await supabase
          .from('classes')
          .select('id, name')
          .eq('organization_id', organizationId)
          .order('name');

        if (classesError) throw classesError;
        setClasses(classesData || []);

        // Load form templates
        const { data: templatesData, error: templatesError } = await supabase
          .from('form_templates')
          .select('*')
          .eq('organization_id', organizationId)
          .eq('is_active', true)
          .order('name');

        if (templatesError) throw templatesError;
        setFormTemplates(templatesData || []);

      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Failed to load families and classes');
      } finally {
        setLoadingFamilies(false);
      }
    };

    loadData();
  }, [organizationId, open]);

  // Reset form when modal closes, or set preselected template when opens
  useEffect(() => {
    if (!open) {
      setTitle('');
      setDescription('');
      setDeadline(undefined);
      setSelectedFamilies([]);
      setSelectAllFamilies(false);
      setDocumentType('custom');
      setSelectedClass('');
      setFamilySearch('');
      setSigningMode('upload');
      setSelectedTemplate('');
    } else if (preselectedTemplate) {
      // Set up form for preselected template
      setSigningMode('electronic');
      setSelectedTemplate(preselectedTemplate.id);
      setTitle(preselectedTemplate.name);
      setDescription(preselectedTemplate.description || '');
    }
  }, [open, preselectedTemplate]);

  // Handle document type selection
  const handleDocumentTypeChange = (type: CommonDocumentType | 'custom') => {
    setDocumentType(type);
    if (type !== 'custom') {
      setTitle(type);
      // Set common descriptions
      const descriptions: Partial<Record<CommonDocumentType, string>> = {
        'Medical Forms': 'Please submit completed medical forms including allergies, medications, and emergency medical information.',
        'Emergency Contact Information': 'Please provide updated emergency contact information for all children.',
        'Photo/Video Release Forms': 'Please sign the photo/video release form to allow us to use images for promotional purposes.',
        'Liability Waivers': 'Please complete and sign the liability waiver for all participating children.',
        'Insurance Information': 'Please provide current insurance information including policy numbers and coverage details.'
      };
      setDescription(descriptions[type] || '');
    }
  };

  // Handle template selection
  const handleTemplateSelection = (templateId: string) => {
    setSelectedTemplate(templateId);
    const template = formTemplates.find(t => t.id === templateId);
    if (template) {
      setTitle(template.name);
      setDescription(template.description || '');
      setSigningMode('electronic'); // Forms always use electronic signing
    }
  };

  // Handle class selection for bulk family assignment
  const handleClassSelection = async (classId: string) => {
    if (!classId || classId === 'none') {
      setSelectedClass('');
      return;
    }

    setSelectedClass(classId);
    
    try {
      // Get families with students in this class
      const { data: enrollments, error } = await supabase
        .from('enrollments')
        .select(`
          student:students(family_id)
        `)
        .eq('class_id', classId)
        .eq('status', 'active');

      if (error) throw error;

      const classFamilyIds = Array.from(new Set(
        enrollments?.map(e => e.student.family_id).filter(Boolean) || []
      ));

      setSelectedFamilies(classFamilyIds);
      setSelectAllFamilies(classFamilyIds.length === families.length);
    } catch (error) {
      console.error('Error loading class families:', error);
      toast.error('Failed to load families for selected class');
    }
  };

  // Handle select all families
  const handleSelectAllFamilies = (checked: boolean) => {
    setSelectAllFamilies(checked);
    if (checked) {
      setSelectedFamilies(families.map(f => f.id));
    } else {
      setSelectedFamilies([]);
    }
  };

  // Handle individual family selection
  const handleFamilySelection = (familyId: string, checked: boolean) => {
    if (checked) {
      setSelectedFamilies(prev => [...prev, familyId]);
    } else {
      setSelectedFamilies(prev => prev.filter(id => id !== familyId));
      setSelectAllFamilies(false);
    }
  };

  // Filter families based on search
  const filteredFamilies = families.filter(family =>
    family.name.toLowerCase().includes(familySearch.toLowerCase())
  );

  // Handle form submission
  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error('Please enter a title for the document request');
      return;
    }

    if (signingMode === 'electronic' && !selectedTemplate) {
      toast.error('Please select a form template for electronic signing');
      return;
    }

    // Check envelope quota for electronic forms
    if (signingMode === 'electronic') {
      try {
        const { data: quotaCheck, error: quotaError } = await supabase.rpc('check_envelope_quota', { 
          org_id: organizationId 
        });
        
        if (quotaError) {
          console.error('Error checking envelope quota:', quotaError);
        } else if (quotaCheck && quotaCheck.length > 0) {
          const quota = quotaCheck[0];
          if (!quota.can_create_envelope) {
            toast.error(`Envelope limit reached (${quota.envelopes_used}/${quota.envelopes_limit}). Please upgrade your plan to send more electronic forms.`);
            return;
          }
          
          // Warn when approaching limit
          if (quota.usage_percentage >= 80) {
            const proceed = window.confirm(
              `You've used ${quota.usage_percentage}% of your envelope quota (${quota.envelopes_used}/${quota.envelopes_limit}). Continue creating this electronic form?`
            );
            if (!proceed) return;
          }
        }
      } catch (error) {
        console.error('Error checking envelope quota:', error);
        // Don't block creation if quota check fails
      }
    }

    if (selectedFamilies.length === 0) {
      toast.error('Please select at least one family');
      return;
    }

    if (!organizationId || !user?.id) {
      toast.error('Organization or user information missing');
      return;
    }

    setLoading(true);
    try {
      const requestData: CreateDocumentRequestData = {
        title: title.trim(),
        description: description.trim() || undefined,
        deadline: deadline ? deadline.toISOString().split('T')[0] : undefined,
        family_ids: selectedFamilies,
        organization_id: organizationId,
        created_by: user.id,
        signing_mode: signingMode === 'electronic' ? 'form' : 'upload',
        template_id: signingMode === 'electronic' && selectedTemplate ? selectedTemplate : undefined
      };

      const result = await DocumentRequestService.createDocumentRequest(requestData);

      if (result.success) {
        // For electronic forms, automatically send the request
        if (signingMode === 'electronic' && result.data?.id) {
          const sendResult = await DocumentRequestService.sendDocumentRequest(result.data.id);
          if (sendResult.success) {
            toast.success('Electronic form request sent to families');
          } else {
            toast.error('Request created but failed to send emails');
          }
        } else {
          toast.success('Document request created successfully');
        }
        
        onSuccess();
        onOpenChange(false);
      } else {
        toast.error(result.error || 'Failed to create document request');
      }
    } catch (error) {
      console.error('Error creating document request:', error);
      toast.error('Failed to create document request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Document Request</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Signing Mode Selection */}
          <Tabs value={signingMode} onValueChange={(value) => setSigningMode(value as 'upload' | 'electronic')}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="upload" className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Upload Documents
              </TabsTrigger>
              <TabsTrigger value="electronic" className="flex items-center gap-2">
                <PenTool className="h-4 w-4" />
                Electronic Forms
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="upload" className="space-y-4">
              {/* Document Type Selection for Upload Mode */}
              <div className="space-y-2">
                <Label htmlFor="documentType">Document Type</Label>
                <Select 
                  value={documentType} 
                  onValueChange={(value) => handleDocumentTypeChange(value as CommonDocumentType | 'custom')}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">Custom Document</SelectItem>
                    {COMMON_DOCUMENT_TYPES.map(type => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>
            
            <TabsContent value="electronic" className="space-y-4">
              {/* Template Selection for Electronic Mode */}
              <div className="space-y-2">
                <Label htmlFor="template">Select Form Template</Label>
                {formTemplates.length > 0 ? (
                  <Select value={selectedTemplate} onValueChange={handleTemplateSelection}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a form template" />
                    </SelectTrigger>
                    <SelectContent>
                      {formTemplates.map(template => (
                        <SelectItem key={template.id} value={template.id}>
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4" />
                            <div>
                              <span className="font-medium">{template.name}</span>
                              {template.description && (
                                <span className="text-xs text-muted-foreground ml-2">
                                  - {template.description}
                                </span>
                              )}
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="p-4 border border-dashed rounded-lg text-center">
                    <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      No form templates available. Create templates in the Form Templates section first.
                    </p>
                  </div>
                )}
              </div>
              
              {selectedTemplate && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <PenTool className="h-4 w-4 text-blue-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-900">Electronic Signature Mode</p>
                      <p className="text-blue-700">
                        Families will receive a form to fill out and sign electronically.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={signingMode === 'electronic' ? "Will be auto-filled from template" : "e.g., Medical Forms for Summer Camp 2025"}
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={signingMode === 'electronic' ? "Will be auto-filled from template" : "Provide detailed instructions about what documents are needed..."}
              rows={3}
            />
          </div>

          {/* Deadline */}
          <div className="space-y-2">
            <Label>Deadline (Optional)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !deadline && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {deadline ? format(deadline, "PPP") : "Select deadline"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={deadline}
                  onSelect={setDeadline}
                  disabled={(date) => date < new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Class Selection for Bulk Assignment */}
          <div className="space-y-2">
            <Label>Quick Select by Class (Optional)</Label>
            <Select value={selectedClass} onValueChange={handleClassSelection}>
              <SelectTrigger>
                <SelectValue placeholder="Select a class to auto-select families" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No class selected</SelectItem>
                {classes.map(classItem => (
                  <SelectItem key={classItem.id} value={classItem.id}>
                    {classItem.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Family Selection */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Select Families *</Label>
              <div className="text-sm text-muted-foreground">
                {selectedFamilies.length} of {families.length} selected
              </div>
            </div>

            {/* Search families */}
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search families..."
                value={familySearch}
                onChange={(e) => setFamilySearch(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Select all checkbox */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="selectAll"
                checked={selectAllFamilies}
                onCheckedChange={handleSelectAllFamilies}
                disabled={loadingFamilies}
              />
              <Label htmlFor="selectAll" className="text-sm font-medium">
                Select all families ({families.length})
              </Label>
            </div>

            {/* Family list */}
            <div className="border rounded-md p-3 max-h-64 overflow-y-auto space-y-2">
              {loadingFamilies ? (
                <div className="text-center py-4 text-muted-foreground">
                  Loading families...
                </div>
              ) : filteredFamilies.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  {familySearch ? 'No families match your search' : 'No families found'}
                </div>
              ) : (
                filteredFamilies.map(family => (
                  <div key={family.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={family.id}
                      checked={selectedFamilies.includes(family.id)}
                      onCheckedChange={(checked) => 
                        handleFamilySelection(family.id, checked as boolean)
                      }
                    />
                    <Label htmlFor={family.id} className="flex-1 text-sm cursor-pointer">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{family.name}</span>
                          <div className="text-xs text-muted-foreground">
                            <Users className="inline h-3 w-3 mr-1" />
                            {family.student_count || 0} students
                          </div>
                        </div>
                        {family.children && family.children.length > 0 && (
                          <div className="text-xs text-muted-foreground">
                            {family.children.map(child => 
                              child.preferred_name || child.first_name
                            ).join(', ')}
                          </div>
                        )}
                      </div>
                    </Label>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Creating...' : 'Create Request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateDocumentRequestModal;